import { Button, FluentProvider, webLightTheme } from "@fluentui/react-components";
import { useBoolean } from "@fluentui/react-hooks";
import { PropsWithChildren, useCallback, useMemo } from "react";
import { defaultButtonConfig, defaultPanelConfig } from "./Constants/ButtonWithPanel.constants";
import CustomPanel from "./CustomPanel/CustomPanel";
import { ButtonWithPanelProps, CustomButtonProps } from "./Interfaces/ButtonWithPanel.interfaces";
import { CustomPanelProps } from "./Interfaces/CustomPanel.interfaces";
import { buttonWithPanelStyles } from "./Styles/ButtonWithPanel.styles";

/**
 * A reusable button component that opens a customizable panel when clicked.
 * Combines Button and Panel functionality for a seamless user experience.
 */

export const ButtonWithPanel = ({
  onPanelOpen,
  buttonProps = {},
  panelProps = defaultPanelConfig,
  children
}: PropsWithChildren<ButtonWithPanelProps>) => {
  const [enablePanel, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);

  // Memoized button configuration
  const buttonConfig = useMemo(
    (): CustomButtonProps => ({
      appearance: buttonProps.appearance ?? defaultButtonConfig.appearance,
      buttonContent: buttonProps.buttonContent ?? defaultButtonConfig.buttonContent,
      disabled: buttonProps.disabled ?? defaultButtonConfig.disabled,
      icon: buttonProps.icon ?? defaultButtonConfig.icon,
      // Safely merge button styles to prevent runtime errors
      style: {
        ...buttonWithPanelStyles.button,
        ...(buttonProps.style ?? {})
      }
    }),
    [buttonProps]
  );

  // Memoized panel configuration
  const panelConfig = useMemo(
    (): CustomPanelProps => ({
      ...defaultPanelConfig,
      ...panelProps,
      enablePanel,
      dismissPanel,
      // Safely merge header styles to prevent runtime errors
      headerStyle: {
        ...buttonWithPanelStyles.defaultHeaderStyle,
        ...(panelProps.headerStyle ?? {})
      }
    }),
    [panelProps, enablePanel, dismissPanel]
  );

  // Optimized panel open handler
  const handleOpenPanel = useCallback(() => {
    openPanel();
    onPanelOpen?.();
  }, [openPanel, onPanelOpen]);

  return (
    <FluentProvider theme={webLightTheme}>
      <Button
        appearance={buttonConfig.appearance}
        disabled={buttonConfig.disabled}
        icon={buttonConfig.icon}
        onClick={handleOpenPanel}
        style={buttonConfig.style}
      >
        {buttonConfig.buttonContent}
      </Button>

      <CustomPanel {...panelConfig}>{children ?? panelProps.panelContent}</CustomPanel>
    </FluentProvider>
  );
};

export default ButtonWithPanel;
